// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { createBffWebSocket, type BffWebSocketClient } from '@/services/bffWebSocket';

export type HoleSocket = {
  /** The screen's own connection, or null while the screen is not the focused route. */
  socket: BffWebSocketClient | null;
  /** The same connection, for callbacks that must read it when they run rather than when they render. */
  socketRef: RefObject<BffWebSocketClient | null>;
};

/**
 * The hole screen's own BFF connection, alive only while the screen is the focused route.
 *
 * React Navigation keeps every screen in the stack mounted, and on web nothing detaches the
 * ones underneath. Entering a round pushes a fresh hole screen (round start, select games, the
 * tour leaderboard, an invite), so a player who leaves the round and comes back has two hole
 * screens alive, and every excursion adds one — on 2026-09-17 one phone ran five at once. With
 * the socket tied to mount, each of them held a connection, rejoined the round on every
 * reconnect and re-rendered on every broadcast, for hours.
 *
 * Tied to focus instead, a blurred screen is inert: its socket is disposed on blur and a new one
 * is created on refocus, which the subscription effects treat exactly like a fresh mount
 * (round.join + round.requestState). Callers key their effects on `socket`, so they re-run for
 * the new connection.
 */
export const useHoleSocket = ({
  apiBaseUrl,
  isFocused,
}: {
  apiBaseUrl: string;
  isFocused: boolean;
}): HoleSocket => {
  const [socket, setSocket] = useState<BffWebSocketClient | null>(null);
  const socketRef = useRef<BffWebSocketClient | null>(null);

  useEffect(() => {
    if (!isFocused) return undefined;
    const created = createBffWebSocket(apiBaseUrl);
    socketRef.current = created;
    setSocket(created);
    return () => {
      created.dispose();
      if (socketRef.current === created) socketRef.current = null;
      setSocket((current) => (current === created ? null : current));
    };
  }, [apiBaseUrl, isFocused]);

  return { socket, socketRef };
};
